/**
 * ModalControl
 * A trigger button that opens a modal dialog containing child controls.
 * In view mode, renders children inline (no trigger button).
 */

import React, { useState, memo } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import { CloseIcon } from '@atlas-forms/ui-components-react';

export interface ModalControlProps {
  control: FormControl & { children?: FormControl[] };
  renderChild: (child: FormControl) => React.ReactNode;
  mode?: 'edit' | 'view';
}

export const ModalControl = memo(function ModalControl({ control, renderChild, mode = 'edit' }: ModalControlProps) {
  const [open, setOpen] = useState(false);
  const cfg = (control as any).config ?? {};
  const children = control.children ?? [];
  const triggerLabel = cfg.triggerLabel ?? control.label ?? 'Open';

  if (mode === 'view') {
    return (
      <div data-control-id={control.id} data-control-type="modal">
        {children.map((child) => (
          <React.Fragment key={child.id}>{renderChild(child)}</React.Fragment>
        ))}
      </div>
    );
  }

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const dialogStyle: React.CSSProperties = {
    background: '#1e293b',
    border: '1px solid rgba(51,65,85,0.6)',
    borderRadius: 12,
    padding: 24,
    minWidth: 400,
    maxWidth: '90vw',
    maxHeight: '80vh',
    overflowY: 'auto',
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 600,
    color: '#f8fafc',
  };

  const closeStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    display: 'flex',
    alignItems: 'center',
    padding: 4,
    borderRadius: 4,
  };

  const triggerStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: 6,
    border: '1px solid rgba(51,65,85,0.4)',
    background: 'rgba(51,65,85,0.2)',
    color: '#f8fafc',
    fontSize: 14,
    cursor: 'pointer',
  };

  return (
    <div data-control-id={control.id} data-control-type="modal">
      <button type="button" onClick={() => setOpen(true)} style={triggerStyle}>
        {triggerLabel}
      </button>
      {open && (
        <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div style={dialogStyle} role="dialog" aria-modal="true" aria-label={control.label}>
            <div style={headerStyle}>
              <span style={titleStyle}>{control.label ?? 'Dialog'}</span>
              <button type="button" onClick={() => setOpen(false)} style={closeStyle} aria-label="Close">
                <CloseIcon width={18} height={18} />
              </button>
            </div>
            <div>
              {children.length > 0
                ? children.map((child) => (
                    <React.Fragment key={child.id}>{renderChild(child)}</React.Fragment>
                  ))
                : <span style={{ fontSize: 13, opacity: 0.5, color: '#94a3b8' }}>No content</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
